import React from "react"
import styled, { css } from "styled-components"
import { up, down } from "styled-breakpoints"
import colors from "@constants/colors"
import {
  DESKTOP_HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
} from "@constants/measurements"
import { body } from "@constants/typography"
import {
  MagnifyingGlass,
  Menu,
  Phone,
  ShoppingCart,
  UserOutline,
} from "@components/icons"
import {
  buttonProps,
  Button,
  IconWrapper,
  LogoComponent,
} from "@components/icons/utils"
import CountBubble from "./CountBubble"
import MenuBar from "./components/MenuDesktop/MenuBar"
import { MenuStates } from "@components/MenuDesktop/common"
import Link from "next/link"
import { useRouter } from "next/router"

const Container = styled.header<{ boxShadow?: boolean }>`
  ${({ boxShadow }) => css`
    z-index: 11;
    justify-content: center;
    display: flex;
    width: 100%;
    height: ${MOBILE_HEADER_HEIGHT};
    padding: 0 20px;
    border-bottom: 1px solid ${colors.divider};
    background-color: ${colors.black};
    position: sticky;
    top: 0;

    ${up("laptop")} {
      height: auto;
      padding: 0 20px;
      border-bottom: none;
      box-shadow: ${boxShadow ? "0px 4px 100px rgba(0, 0, 0, 0.1)" : "none"};

      ${Button} {
        margin: 0 10px;
      }

      .rightNav {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
        flex-shrink: 0;

        &.top-right-nav {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          align-items: flex-end;
        }
      }

      ${up("desktop")} {
        padding: 0 42px;

        ${Button} {
          margin: 0 16px;
        }
      }
    }
  `}
`

const DesktopContent = styled.div`
  display: none;

  ${up("laptop")} {
    align-items: stretch;
    justify-content: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 14px 0 12px;
  }
`

const DesktopRow = styled.div`
  ${up("laptop")} {
    align-items: center;
    display: flex;
    justify-content: space-between;
    position: relative;
    width: 100%;

    &.top-row {
      justify-content: left;
    }

    &.bottom-row {
      justify-content: center;
    }
  }
`

const MobileContent = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;

  ${up("laptop")} {
    display: none;
  }

  [class*="NavbarHeader__Section"] {
    [class*="utils__IconWrapper"] {
      svg {
        transform: scale(1.2);
        fill: ${colors.snow};
      }
    }
  }
`

const Section = styled.div`
  align-items: center;
  display: flex;
  transform: translateX(12px);
  color: ${colors.snow};
  font-family: "Jost", sans-serif;

  &.left-section {
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;

    &.top-nav {
      flex: 0 1 auto;
      transform: none;
      gap: 20px;
    }

    &.bottom-nav {
      flex: 0 1 auto;
      transform: none;
    }
  }

  &.quicklinks {
    justify-content: flex-end;
    gap: 16px;
    margin-bottom: 0px;
    font-size: 13px;
    text-transform: uppercase;
    white-space: nowrap;

    ${up("desktop")} {
      gap: 20px;
      font-size: 13px;
    }
  }

  &.utility-top-links {
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1;
    width: 100%;

    a {
      color: ${colors.snow};
    }

    ${up("desktop")} {
      gap: 20px;
      font-size: 13px;
    }
  }

  &.bottom-quicklinks {
    gap: 8px;
    font-size: 14px;
    font-weight: 600;

    a {
      color: ${colors.snow};
      padding-bottom: 10px;
      white-space: nowrap;
    }

    ${up("desktop")} {
      gap: 10px;
      font-size: 16px;
    }
  }

  &.icons {
    justify-content: flex-end;
    gap: 8px;
    padding-bottom: 10px;
    white-space: nowrap;

    ${up("desktop")} {
      gap: 12px;
    }

    > div {
      max-width: 40px;
      &.contactDiv {
        max-width: none;
      }
    }
  }

  &.utility-contact {
    justify-content: flex-end;
    padding-bottom: 0;
  }

  .contactDiv {
    text-transform: uppercase;
    text-align: right;
    white-space: nowrap;

    .label {
      color: #b5b5b5;
      text-align: right;
      font-size: 10px;
      letter-spacing: 0.04em;

      ${up("desktop")} {
        font-size: 10px;
      }
    }

    .phone {
      text-decoration: underline;
      text-align: right;
      font-size: 13px;
      line-height: 1.1;

      ${up("desktop")} {
        font-size: 14px;
      }
    }
  }

  [class*="utils__IconWrapper"] {
    svg {
      transform: scale(1.2);
      fill: ${colors.snow};
    }
  }
`

const CenterSection = styled.div`
  position: absolute;
  left: 50%;
  display: flex;
  transform: translateX(-50%);
`

const LinkButton = styled.a`
  ${buttonProps}

  ${up("laptop")} {
    margin: 0 16px;
  }
`

const AccountButton = styled.button`
  ${buttonProps}
  ${body}
    width: initial;
  height: initial;
  margin-left: 20px;
  font-size: 14px;
  color: ${colors.slateDark};

  svg {
    *,
    path {
      fill: ${colors.snow};
    }
  }
`

const User = styled.div`
  ${up("laptop")} {
    width: 88px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    white-space: nowrap;
  }
`

const NavContainer = styled.div`
  display: flex;
  align-items: ;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
`

const StackedMenus = styled.div`
  display: flex;
  flex-direction: column;
`

export interface HeaderProps {
  headerRef?: any
  logoSrc?: string
  user?: any
  cartCount?: number
  menuState?: undefined | MenuStates
  boxShadow?: boolean
  navbarRightColumnLinks: Array<{
    label: string
    link: string
    module?: {
      name: string
    }
  }>
  module?: string
  onClickSearch?: (() => void) | React.ReactEventHandler
  onClickCart?: (() => void) | React.ReactEventHandler
  onResetVehicleSelectorMenu?: () => void
  onClickVehicleSelectorMenu?: () => void
  onClickProSeriesMenu?: () => void
  onClickPSSeatsMenu?: () => void
  onClickPSArmorMenu?: () => void
  onClickMainMenu?: () => void
  onClickMenu?: (() => void) | React.ReactEventHandler
  onClickLogIn?: (() => void) | React.ReactEventHandler
}

const Header: React.FC<HeaderProps> = ({
  headerRef,
  logoSrc,
  user,
  cartCount,
  boxShadow,
  menuState,
  navbarRightColumnLinks,
  module = "automotive",
  onClickSearch,
  onClickCart,
  onResetVehicleSelectorMenu,
  onClickVehicleSelectorMenu,
  onClickProSeriesMenu,
  onClickPSSeatsMenu,
  onClickPSArmorMenu,
  onClickMainMenu,
  onClickMenu,
  onClickLogIn,
}) => {
  const router = useRouter()
  const isPowersports = router.asPath.includes("/powersports")
  const filteredNavbarLinks = navbarRightColumnLinks.filter((link) => {
    if (isPowersports && link.link.toLowerCase().includes("faq")) {
      return false
    }
    return (
      !module ||
      module.toLowerCase() == link.module?.name.toLowerCase() ||
      link.module?.name.toLowerCase() == "all"
    )
  })
  const topRowLinks = filteredNavbarLinks
  // Hide cart on upholstery pages only (inventory checker, configurator, etc.)
  const isUpholsteryPage = router.asPath.includes("upholstery")

  return (
    <Container ref={headerRef} boxShadow={boxShadow}>
      <DesktopContent>
        <DesktopRow className="top-row">
          <Section className="left-section top-nav">
            <NavContainer>
              <LogoComponent src={logoSrc} module={module} />

              <StackedMenus>
                <MenuBar module={module} view="toggle" stacked />

                <MenuBar
                  menuState={menuState}
                  onResetVehicleSelectorMenu={onResetVehicleSelectorMenu}
                  onClickVehicleSelectorMenu={onClickVehicleSelectorMenu}
                  onClickProSeriesMenu={onClickProSeriesMenu}
                  onClickPSSeatsMenu={onClickPSSeatsMenu}
                  onClickPSArmorMenu={onClickPSArmorMenu}
                  onClickMainMenu={onClickMainMenu}
                  module={module}
                  view="actions"
                  stacked
                  centered
                />
              </StackedMenus>
            </NavContainer>
          </Section>
          <div className="rightNav top-right-nav">
            <Section className="quicklinks utility-top-links">
              {topRowLinks.map((link) => (
                <Link
                  key={link.link}
                  href={
                    (module.toLowerCase() == "automotive" ||
                    link.module?.name.toLowerCase() != "all"
                      ? ""
                      : "/" + module) + link.link
                  }
                  passHref
                  prefetch={false}
                >
                  {link.label}
                </Link>
              ))}
            </Section>
            <Section className="icons utility-contact">
              <div className="contactDiv">
                <div className="label">Give us a call</div>
                <div className="phone">1.888.460.0640</div>
              </div>
              <Button onClick={onClickSearch}>
                <IconWrapper>
                  <MagnifyingGlass
                    strokeColor={colors.snow}
                    strokeWidth={0}
                    width="24px"
                    height="24px"
                  />
                </IconWrapper>
              </Button>
              {!isUpholsteryPage && (
                <Button onClick={onClickCart}>
                  {cartCount !== undefined && <CountBubble count={cartCount} />}
                  <IconWrapper>
                    <ShoppingCart
                      strokeColor={colors.snow}
                      strokeWidth={0}
                      width="24px"
                      height="24px"
                    />
                  </IconWrapper>
                </Button>
              )}
            </Section>
          </div>
        </DesktopRow>
        {/* <DesktopRow className="bottom-row">
          <Section className="left-section bottom-nav">
            <MenuBar
              menuState={menuState}
              onResetVehicleSelectorMenu={onResetVehicleSelectorMenu}
              onClickVehicleSelectorMenu={onClickVehicleSelectorMenu}
              onClickProSeriesMenu={onClickProSeriesMenu}
              onClickPSSeatsMenu={onClickPSSeatsMenu}
              onClickPSArmorMenu={onClickPSArmorMenu}
              onClickMainMenu={onClickMainMenu}
              module={module}
              view="actions"
              stacked
              centered
            />
          </Section>
        </DesktopRow> */}
      </DesktopContent>
      <MobileContent>
        <LogoComponent src={logoSrc} module={module} />
        <Section>
          <Button onClick={onClickSearch}>
            <IconWrapper>
              <MagnifyingGlass strokeWidth={0} width="24px" height="24px" />
            </IconWrapper>
          </Button>
          {!isUpholsteryPage && (
            <LinkButton onClick={onClickCart}>
              {cartCount !== undefined && <CountBubble count={cartCount} />}
              <IconWrapper>
                <ShoppingCart strokeWidth={0} width="24px" height="24px" />
              </IconWrapper>
            </LinkButton>
          )}
          <Button style={{ marginLeft: "8px" }}>
            <a href="tel:1-888-460-0640">
              <IconWrapper>
                <Phone style={{ fill: "none" }} width="24px" height="24px" />
              </IconWrapper>
            </a>
          </Button>
          <Button style={{ marginLeft: "8px" }} onClick={onClickMenu}>
            <IconWrapper style={{ width: "26px", height: "26px" }}>
              <Menu strokeWidth={1} strokeColor={colors.snow} />
            </IconWrapper>
          </Button>
        </Section>
      </MobileContent>
    </Container>
  )
}

export default Header
